import React from "react";
import styled from "styled-components";
import { useLoadingIndicator } from "../../common/context/LoadingIndicatorContext";
import { useTranslation } from "react-i18next";
import { SchoolStudent } from "../../common/api";
import { Lesson } from "../../common/api";
import { Button, Form, Modal, Row } from "react-bootstrap";
import { ApiContext } from "../../common/context/ApiContext";
import {
  COLOR_TEXT_PRIMARY,
  COLOR_GRAY_INACTIVE,
  COLOR_SHADOW_LIGHT,
  COLOR_WHITE,
} from "../../common/colors";

const CancelButton = styled(Button)`
  border: none;
  color: ${COLOR_TEXT_PRIMARY};
  background-color: ${COLOR_WHITE};

  :hover {
    color: ${COLOR_TEXT_PRIMARY};
    background-color: ${COLOR_WHITE};
  }
`;

const UnassignButton = styled(Button)`
  border: none;
  background-color: ${COLOR_TEXT_PRIMARY};
  border-radius: 8px;

  :disabled {
    background-color: ${COLOR_GRAY_INACTIVE};
  }
`;

export const SingleLessonUnassignModal: React.FC<{
  lesson: Lesson;
  customFlashpackId: number | null;
  show: boolean;
  onClose: (hasAssignments: boolean | null) => void;
}> = ({ lesson, customFlashpackId, show, onClose }) => {
  const { t } = useTranslation();
  const { startLoading, stopLoading } = useLoadingIndicator(false);
  const { getSingleLessonAssignments, deleteSingleLessonAssignments } =
    React.useContext(ApiContext);
  const prevShow = React.useRef(show);
  const [selectedStudents, setSelectedStudents] = React.useState<Set<number>>(
    new Set()
  );
  const [studentList, setStudentList] = React.useState<SchoolStudent[]>([]);
  const [showMainModal, setShowMainModal] = React.useState(false);
  const [showSuccessModal, setShowSuccessModal] = React.useState(false);
  const [hasAssignments, setHasAssignments] = React.useState<boolean | null>(
    null
  );

  const labelForStudent = (s: Readonly<SchoolStudent>) =>
    s.roster_first_name + " " + s.roster_last_name + ".";

  const allSelected =
    studentList.length > 0 && selectedStudents.size === studentList.length;

  const onChangeStudentCheckbox = React.useCallback(
    (profileId: number, isChecked: boolean) => {
      const newSelectedStudents = new Set(selectedStudents);
      if (isChecked) {
        newSelectedStudents.add(profileId);
      } else {
        newSelectedStudents.delete(profileId);
      }
      setSelectedStudents(newSelectedStudents);
    },
    [selectedStudents, setSelectedStudents]
  );

  const onSelectAll = React.useCallback(
    (isChecked: boolean) => {
      if (isChecked) {
        setSelectedStudents(new Set(studentList.map((s) => s.id)));
      } else {
        setSelectedStudents(new Set());
      }
    },
    [studentList]
  );

  const onCloseAllModals = React.useCallback(() => {
    setShowMainModal(false);
    setShowSuccessModal(false);
    onClose(hasAssignments);
  }, [onClose, setShowMainModal, hasAssignments]);

  const unassignSelected = React.useCallback(() => {
    startLoading();
    deleteSingleLessonAssignments(
      lesson.id,
      customFlashpackId,
      Array.from(selectedStudents)
    ).then((_) => {
      stopLoading();
      setShowMainModal(false);
      setShowSuccessModal(true);
      setHasAssignments(studentList.length > selectedStudents.size);
    });
  }, [
    lesson,
    customFlashpackId,
    startLoading,
    stopLoading,
    deleteSingleLessonAssignments,
    selectedStudents,
    setHasAssignments,
    studentList,
  ]);

  React.useEffect(() => {
    // Run this only when going from hidden to visible
    if (!prevShow.current && show) {
      startLoading();
      getSingleLessonAssignments(lesson.id, customFlashpackId).then(
        ({ student_list }) => {
          // Reinitialize everything
          stopLoading();
          setStudentList(student_list);
          setShowSuccessModal(false);
          setSelectedStudents(new Set());
          setShowMainModal(true);
        }
      );
    }
    prevShow.current = show;
  }, [
    show,
    lesson,
    customFlashpackId,
    startLoading,
    stopLoading,
    getSingleLessonAssignments,
    setShowMainModal,
    setSelectedStudents,
  ]);

  return (
    <>
      <Modal show={showMainModal} onHide={onCloseAllModals} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            {t("unassign_modal_currently_assigned_students")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="ps-2">
              <Form.Check
                type="checkbox"
                id="select-all-students"
                label={t("unassign_modal_select_all")}
                checked={allSelected}
                onChange={(event) => onSelectAll(event.target.checked)}
              />
            </Row>
            <hr
              style={{
                margin: "8px -4px",
                borderColor: COLOR_SHADOW_LIGHT,
                opacity: 1,
              }}
            />
            {studentList.map((student) => {
              return (
                <Row key={student.id} className="ps-2">
                  <Form.Check
                    type="checkbox"
                    id={`student-${student.id}`}
                    label={labelForStudent(student)}
                    checked={selectedStudents.has(student.id)}
                    onChange={(event) =>
                      onChangeStudentCheckbox(student.id, event.target.checked)
                    }
                  />
                </Row>
              );
            })}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <CancelButton onClick={onCloseAllModals}>
            {t("generic_cancel")}
          </CancelButton>
          <UnassignButton
            disabled={!(selectedStudents.size > 0)}
            onClick={unassignSelected}
          >
            {t("unassign_modal_unassign_selected")}
          </UnassignButton>
        </Modal.Footer>
      </Modal>
      {/* Success dialog */}
      <Modal
        show={showSuccessModal}
        onHide={onCloseAllModals}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("generic_success")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("unassign_modal_success", { count: selectedStudents.size })}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={onCloseAllModals}>
            {t("generic_ok")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
